/**
 * SMART LINK NG — Biometric & WebAuthn Authentication Service
 * Handles native device fingerprint, Touch ID, Face ID, and Windows Hello
 * registration & verification directly using standard WebAuthn API.
 */

import { SupabaseAuthService } from "./supabaseAuth";

// Helper: Base64URL string to Uint8Array
function base64UrlToBuffer(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper: ArrayBuffer to Base64URL string
function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface BiometricDevice {
  id: string;
  credentialId: string;
  deviceName: string;
  createdAt: string;
  lastUsedAt?: string | null;
}

export class BiometricAuthService {
  /**
   * Check if the user's browser & hardware support Platform Biometrics (Fingerprint, Touch ID, Face ID)
   */
  public static async isBiometricSupported(): Promise<boolean> {
    if (typeof window === "undefined" || !window.PublicKeyCredential) {
      return false;
    }
    try {
      if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function") {
        return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if running in an iframe where browser security policy blocks WebAuthn
   */
  public static isIframeRestricted(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  /**
   * Helper to retrieve active user authentication token across Supabase & Local storage
   */
  public static async getStoredAuthToken(explicitToken?: string): Promise<string> {
    if (explicitToken && typeof explicitToken === "string" && explicitToken.trim()) {
      return explicitToken.trim();
    }
    // 1. Direct tokens
    const directToken =
      localStorage.getItem("smartlink_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("sessionToken");
    if (directToken) return directToken;

    // 2. User profile storage
    try {
      const rawUser = localStorage.getItem("smart_link_user") || localStorage.getItem("smartlink_user");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u.token) return u.token;
        if (u.sessionToken) return u.sessionToken;
        if (u.access_token) return u.access_token;
      }
    } catch {}

    // 3. Supabase active session
    try {
      const supaSess = await SupabaseAuthService.getSession();
      if (supaSess?.access_token) {
        localStorage.setItem("smartlink_token", supaSess.access_token);
        return supaSess.access_token;
      }
    } catch {}

    // 4. Admin session
    try {
      const rawAdmin = localStorage.getItem("admin_session") || localStorage.getItem("smart_link_admin_session");
      if (rawAdmin) {
        const a = JSON.parse(rawAdmin);
        if (a.token) return a.token;
      }
    } catch {}

    return "";
  }

  /**
   * Register and enroll the current device's fingerprint or biometric authenticator
   */
  public static async enrollBiometrics(options?: {
    deviceName?: string;
    token?: string;
  }): Promise<{ success: boolean; message: string; passkey?: any }> {
    const supported = await this.isBiometricSupported();
    if (!supported) {
      throw new Error("Biometric authentication is not supported or not enabled on this device/browser.");
    }

    // 1. Get auth headers
    const storedToken = await this.getStoredAuthToken(options?.token);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (storedToken) {
      headers["Authorization"] = `Bearer ${storedToken}`;
      headers["x-session-token"] = storedToken;
    }

    // 2. Request creation options from backend
    const optRes = await fetch("/api/auth/passkeys/register-options", {
      method: "POST",
      headers,
    });

    if (!optRes.ok) {
      const errJson = await optRes.json().catch(() => ({}));
      throw new Error(errJson.error || "Failed to initialize biometric enrollment");
    }

    const { options: rawOptions } = await optRes.json();
    if (!rawOptions) {
      throw new Error("Invalid biometric options received from server");
    }

    // 3. Format options for navigator.credentials.create
    const publicKeyCreationOptions: PublicKeyCredentialCreationOptions = {
      ...rawOptions,
      challenge: base64UrlToBuffer(rawOptions.challenge),
      user: {
        ...rawOptions.user,
        id: base64UrlToBuffer(rawOptions.user.id),
      },
      excludeCredentials: rawOptions.excludeCredentials?.map((cred: any) => ({
        ...cred,
        id: base64UrlToBuffer(cred.id),
      })),
    };

    // 4. Prompt native fingerprint scanner on device
    let credential: PublicKeyCredential | null = null;
    try {
      credential = (await navigator.credentials.create({
        publicKey: publicKeyCreationOptions,
      })) as PublicKeyCredential;
    } catch (err: any) {
      if (
        err.name === "NotAllowedError" ||
        err.name === "SecurityError" ||
        (err.message && (err.message.includes("publickey-credentials") || err.message.includes("Permissions Policy")))
      ) {
        if (this.isIframeRestricted()) {
          throw new Error(
            "Biometric sensor access is restricted inside embedded preview iframes by browser security policies. Please open the app URL in a standalone browser tab/window to enroll your fingerprint."
          );
        }
      }
      throw new Error(err.message || "Biometric enrollment was cancelled or not recognized.");
    }

    if (!credential) {
      throw new Error("Biometric enrollment was cancelled by user.");
    }

    const response = credential.response as AuthenticatorAttestationResponse;
    const credentialId = bufferToBase64Url(credential.rawId);
    const publicKey = response.getPublicKey ? bufferToBase64Url(response.getPublicKey()!) : credentialId;

    // Detect device name
    const defaultDeviceName =
      options?.deviceName ||
      (/iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? "Apple Device (Touch ID / Face ID)"
        : /Android/i.test(navigator.userAgent)
        ? "Android Biometric Sensor"
        : /Macintosh/i.test(navigator.userAgent)
        ? "Mac Touch ID"
        : /Windows/i.test(navigator.userAgent)
        ? "Windows Hello Fingerprint"
        : "Biometric Device");

    // 5. Send verification back to backend to save into Turso
    const verifyRes = await fetch("/api/auth/passkeys/register-verify", {
      method: "POST",
      headers,
      body: JSON.stringify({
        credentialId,
        publicKey,
        deviceName: defaultDeviceName,
        challenge: rawOptions.challenge,
        transports: typeof (credential.response as any).getTransports === "function" ? (credential.response as any).getTransports() : undefined,
      }),
    });

    if (!verifyRes.ok) {
      const errJson = await verifyRes.json().catch(() => ({}));
      throw new Error(errJson.error || "Failed to verify and save biometric fingerprint");
    }

    const result = await verifyRes.json();
    
    // Mark biometric as enrolled on this device
    try {
      const rawUser = localStorage.getItem("smart_link_user") || localStorage.getItem("smartlink_user");
      const uid = rawUser ? JSON.parse(rawUser)?.id || JSON.parse(rawUser)?.uid : undefined;
      BiometricAuthService.setEnrolled(uid, true);
    } catch {}

    return result;
  }

  /**
   * Authenticate / Sign In using Device Fingerprint
   */
  public static async authenticateWithBiometrics(
    email?: string
  ): Promise<{ success: boolean; user: any; token: string; message: string }> {
    const supported = await this.isBiometricSupported();
    if (!supported) {
      throw new Error("Biometric sign-in is not supported on this device/browser.");
    }

    // Auto-resolve remembered email if not explicitly provided
    let targetEmail = email && typeof email === "string" ? email.trim() : undefined;
    if (!targetEmail) {
      try {
        const saved = localStorage.getItem("smartlink_saved_email");
        if (saved && saved.trim()) {
          targetEmail = saved.trim();
        } else {
          const rawUser = localStorage.getItem("smart_link_user") || localStorage.getItem("smartlink_user");
          if (rawUser) {
            const parsed = JSON.parse(rawUser);
            if (parsed.email) targetEmail = parsed.email.trim();
          }
        }
      } catch {}
    }

    // 1. Request login challenge from backend
    const optRes = await fetch("/api/auth/passkeys/login-options", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: targetEmail }),
    });

    if (!optRes.ok) {
      const errJson = await optRes.json().catch(() => ({}));
      throw new Error(errJson.error || "Failed to initialize biometric challenge");
    }

    const { options: rawOptions } = await optRes.json();
    if (!rawOptions) {
      throw new Error("Invalid biometric login challenge");
    }

    // 2. Format options for navigator.credentials.get
    const publicKeyRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64UrlToBuffer(rawOptions.challenge),
      timeout: rawOptions.timeout || 60000,
      rpId: rawOptions.rpId || window.location.hostname,
      userVerification: rawOptions.userVerification || "required",
      allowCredentials: rawOptions.allowCredentials?.map((cred: any) => ({
        ...cred,
        id: base64UrlToBuffer(cred.id),
      })),
    };

    // 3. Prompt native fingerprint reader
    let assertion: PublicKeyCredential | null = null;
    try {
      assertion = (await navigator.credentials.get({
        publicKey: publicKeyRequestOptions,
      })) as PublicKeyCredential;
    } catch (err: any) {
      if (
        err.name === "NotAllowedError" ||
        err.name === "SecurityError" ||
        (err.message && (err.message.includes("publickey-credentials") || err.message.includes("Permissions Policy")))
      ) {
        if (this.isIframeRestricted()) {
          throw new Error(
            "Biometric sensor access is restricted inside embedded preview iframes by browser security policies. Please open the app in a standalone tab to sign in with fingerprint, or use your email and password."
          );
        }
      }
      throw new Error(err.message || "Biometric sign-in was cancelled or not recognized.");
    }

    if (!assertion) {
      throw new Error("Biometric sign-in was cancelled.");
    }

    const credentialId = bufferToBase64Url(assertion.rawId);

    // 4. Verify signature on backend & log in
    const verifyRes = await fetch("/api/auth/passkeys/login-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        credentialId,
        challenge: rawOptions.challenge,
      }),
    });

    if (!verifyRes.ok) {
      const errJson = await verifyRes.json().catch(() => ({}));
      throw new Error(errJson.error || "Biometric authentication failed. Please try password login.");
    }

    const result = await verifyRes.json();

    if (result.token) {
      localStorage.setItem("smartlink_token", result.token);
      localStorage.setItem("token", result.token);
    }
    if (result.user) {
      localStorage.setItem("smartlink_user", JSON.stringify(result.user));
      localStorage.setItem("smart_link_user", JSON.stringify(result.user));
      if (result.user.email) {
        localStorage.setItem("smartlink_saved_email", result.user.email);
      }
      const uid = result.user.id || result.user.uid;
      BiometricAuthService.setEnrolled(uid, true);
    }

    return result;
  }

  /**
   * Helper to get local enrollment storage keys
   */
  private static getEnrollmentKey(userId?: string): string {
    return `smartlink_biometric_enrolled_${userId || "current"}`;
  }

  private static getDismissedKey(userId?: string): string {
    return `smartlink_biometric_dismissed_${userId || "current"}`;
  }

  /**
   * Check if the user is already enrolled for biometrics locally or on server
   */
  public static async isEnrolled(userId?: string, token?: string): Promise<boolean> {
    // 1. Immediate local storage check
    const localFlag = localStorage.getItem(this.getEnrollmentKey(userId));
    if (localFlag === "true") return true;

    // 2. Server check if token available
    try {
      const passkeys = await this.listRegisteredPasskeys(token);
      if (passkeys && passkeys.length > 0) {
        this.setEnrolled(userId, true);
        return true;
      } else {
        this.setEnrolled(userId, false);
        return false;
      }
    } catch {
      return localFlag === "true";
    }
  }

  /**
   * Mark biometric enrollment status for user
   */
  public static setEnrolled(userId?: string, enrolled: boolean = true): void {
    try {
      if (enrolled) {
        localStorage.setItem(this.getEnrollmentKey(userId), "true");
        localStorage.setItem("smartlink_biometric_enrolled", "true");
        // Clear dismissed flag if newly enrolled
        localStorage.removeItem(this.getDismissedKey(userId));
      } else {
        localStorage.removeItem(this.getEnrollmentKey(userId));
      }
    } catch {}
  }

  /**
   * Check if user explicitly dismissed the activation prompt
   */
  public static isEnrollPromptDismissed(userId?: string): boolean {
    try {
      return localStorage.getItem(this.getDismissedKey(userId)) === "true";
    } catch {
      return false;
    }
  }

  /**
   * Dismiss the biometric prompt so user isn't prompted repetitively
   */
  public static dismissEnrollPrompt(userId?: string): void {
    try {
      localStorage.setItem(this.getDismissedKey(userId), "true");
    } catch {}
  }

  /**
   * List all registered biometric devices for current user
   */
  public static async listRegisteredPasskeys(token?: string): Promise<BiometricDevice[]> {
    try {
      const storedToken = await this.getStoredAuthToken(token);
      const headers: Record<string, string> = {};
      if (storedToken) {
        headers["Authorization"] = `Bearer ${storedToken}`;
        headers["x-session-token"] = storedToken;
      }

      const res = await fetch("/api/auth/passkeys/list", { headers });
      if (!res.ok) return [];
      const json = await res.json();
      const list = json.passkeys || [];
      if (list.length > 0) {
        localStorage.setItem("smartlink_biometric_enrolled", "true");
      }
      return list;
    } catch {
      return [];
    }
  }

  /**
   * Delete a registered biometric device
   */
  public static async deletePasskey(id: string, token?: string): Promise<boolean> {
    try {
      const storedToken = await this.getStoredAuthToken(token);
      const headers: Record<string, string> = {};
      if (storedToken) {
        headers["Authorization"] = `Bearer ${storedToken}`;
        headers["x-session-token"] = storedToken;
      }

      const res = await fetch(`/api/auth/passkeys/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers,
      });

      if (res.ok) {
        // Re-check remaining passkeys
        const remaining = await this.listRegisteredPasskeys(token);
        if (remaining.length === 0) {
          localStorage.removeItem("smartlink_biometric_enrolled");
          try {
            const rawUser = localStorage.getItem("smart_link_user") || localStorage.getItem("smartlink_user");
            const uid = rawUser ? JSON.parse(rawUser)?.id || JSON.parse(rawUser)?.uid : undefined;
            this.setEnrolled(uid, false);
          } catch {}
        }
      }

      return res.ok;
    } catch {
      return false;
    }
  }
}
